import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import {
  IssueClassifySurface,
  IssueReportItem,
  IssueReportSource,
  IssueReportSubject,
} from '../../redux/features/issueReport/issueReport.props';
import {
  selectIsIssueReportingEnabled,
  selectIsIssueReportingHitl7Enabled,
} from '../../redux/features/config/config.selector';
import {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectFlagWindowRemainingMs,
  selectIssueReports,
  selectOutstandingIssueReports,
  selectSessionOutstandingIssueReports,
} from '../../redux/features/issueReport/issueReport.selectors';
import {
  closeFlagWindow,
  commitIssueType,
  flagIssue,
  pauseFlagWindow,
  resumeFlagWindow,
  voidIssue,
} from '../../redux/features/issueReport/issueReport.slice';

/**
 * HITL-1492 Pillar 3, Part A — the single seam the issue-reporting UI consumes.
 *
 * The flag gate lives here, ONCE, so no caller can leak a network call with the
 * feature off. Do NOT re-AND `isEnabled` at a call site.
 */
const useIssueReport = () => {
  const dispatch = useAppDispatch();

  const isEnabled = useSelector(selectIsIssueReportingEnabled);
  /**
   * The gate for the surfaces added on the 7.0 console (REL-8155 / 7.2.0) —
   * `hitl_3_0 AND hitl_issue_reporting AND hitl_7_0`.
   *
   * STRICTLY NARROWER than `isEnabled`, and deliberately not named `isHitl7`:
   * it is not the same thing as the global `selectIsHitl7Enabled`, which says
   * nothing about issue reporting. A surface that renders on the weaker gate
   * would put a 7.2 control on a console that has neither the layout for it nor
   * the feature behind it.
   *
   * Render every new mark behind THIS; keep `isEnabled` for anything that was
   * already live in 6.x.
   */
  const isHitl7IssueReportingEnabled = useSelector(
    selectIsIssueReportingHitl7Enabled
  );

  const reports = useSelector(selectIssueReports);
  const activeReport = useSelector(selectActiveIssueReport);
  const activeWindowExpiresAt = useSelector(selectActiveFlagWindowExpiresAt);
  const remainingMs = useSelector(selectFlagWindowRemainingMs);
  /**
   * Still needing the agent, newest first, SHIFT-scoped — what the badge counts and
   * what every backlog surface lists. Reads no session state, which is why it still
   * returns rows on the task-router lobby.
   */
  const outstandingReports = useSelector(selectOutstandingIssueReports);
  /**
   * The same backlog, narrowed to the CURRENT session — what the console badge and
   * its popover count. Empty between cars, by design.
   *
   * The two are deliberately both on offer: the console asks "what is outstanding on
   * the car in front of me", the task-router lobby asks "what is outstanding at all".
   * A surface that picks the wrong one is either noisy mid-order or empty exactly
   * when the agent has time to clear it.
   */
  const sessionOutstandingReports = useSelector(
    selectSessionOutstandingIssueReports
  );

  /** Returns the new `issue_id`, so HITL-1499's toast can address its own record. */
  const flag = useCallback(
    (
      args: {
        source?: IssueReportSource;
        /** Set when the gesture itself classifies — see the transcript entries. */
        issue_type?: string | null;
        issue_detail?: string | null;
        /**
         * Set when the gesture already knows its item — HITL-1655's cart-row flag, and
         * nothing else. Panel entry points must leave this alone (HITL-1623).
         */
        issue_item?: IssueReportItem | null;
        /** What a non-item gesture is about: a suggestion or the order banner. */
        issue_subject?: IssueReportSubject | null;
      } = {}
    ) => {
      if (!isEnabled) return null;
      return dispatch(flagIssue(args));
    },
    [isEnabled, dispatch]
  );

  const commit = useCallback(
    (args: {
      issue_id: string;
      issue_type: string | null;
      issue_detail?: string | null;
      /** The surface the agent classified from — null when nothing was classified. */
      classified_from?: IssueClassifySurface | null;
      /** The item the agent named, or null for none (HITL-1623). */
      issue_item?: IssueReportItem | null;
    }) => {
      if (!isEnabled) return null;
      return dispatch(commitIssueType(args));
    },
    [isEnabled, dispatch]
  );

  const voidReport = useCallback(
    (issueId: string) => {
      if (!isEnabled) return null;
      return dispatch(voidIssue({ issue_id: issueId }));
    },
    [isEnabled, dispatch]
  );

  /** Detail field gained focus — stop the clock. */
  const pauseWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(pauseFlagWindow());
  }, [isEnabled, dispatch]);

  /** Detail field lost focus — resume from the banked remainder. */
  const resumeWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(resumeFlagWindow());
  }, [isEnabled, dispatch]);

  const closeWindow = useCallback(() => {
    if (!isEnabled) return;
    dispatch(closeFlagWindow());
  }, [isEnabled, dispatch]);

  return {
    isEnabled,
    isHitl7IssueReportingEnabled,
    reports,
    activeReport,
    activeWindowExpiresAt,
    remainingMs,
    outstandingReports,
    sessionOutstandingReports,
    flagIssue: flag,
    commitIssueType: commit,
    voidIssue: voidReport,
    pauseFlagWindow: pauseWindow,
    resumeFlagWindow: resumeWindow,
    closeFlagWindow: closeWindow,
  };
};

export default useIssueReport;
